import { createSelector } from 'reselect';
import createMetadataAPISelectors from '../../utils/api-metadata-selectors';
import moment from 'moment';

const defaultSelectors = createMetadataAPISelectors({
  name: 'contractStatistics'
});

// Returns a function which allows filtering through the user's contracts.
// This function accepts three parameters:
// - timeUnit (shift or hours)
// - periodType (week, month, quarter, year)
// - time: the time which lies somewhere inside the requested period type
// - groupId: the group for which the contract should apply
const selectContractStatisticsFilter = createSelector(defaultSelectors.byId, byId => {
  return (timeUnit, periodType, time, contractId) => {
    const contractData = byId[contractId];
    if (!contractData) return [];

    const key = `${periodType}_${timeUnit}`;
    const unitAndPeriodData = contractData.stats[key] || [];

    return unitAndPeriodData.filter(stats => {
      switch (periodType) {
        case 'year': {
          return moment(time).year() === moment(stats.start_time).year();
        }
        case 'quarter': {
          return (
            moment(time).quarter() === moment(stats.start_time).quarter() &&
            moment(time).year() === moment(stats.start_time).year()
          );
        }
        case 'month': {
          return (
            moment(time).month() === moment(stats.start_time).month() &&
            moment(time).year() === moment(stats.start_time).year()
          );
        }
        case 'week': {
          return (
            moment(time).week() === moment(stats.start_time).week() &&
            moment(time).year() === moment(stats.start_time).year()
          );
        }
      }
      // we want inBetween to be exact without rounding (on e.g. day) so we
      // pass undefined as third parameter. If we don't do this, we may get
      // multiple consecutive periods.
      // If that doesn't give any results, we'll try to round by periodType.
      // This is needed e.g. when we have a fractional contract year. If we
      // look for 2021-01-01, and the contract start time is 2021-09-01,
      // this should still give results.
      // Finally, if the period type is custom, since custom is not a recognized
      // timeUnit by moment, we fall back to day, since we are ignoring hours,
      // we need to use inclusive comparison
      return (
        moment(time).isBetween(stats.start_time, stats.end_time, undefined, '[)') ||
        moment(time).isBetween(stats.start_time, stats.end_time, periodType, '[)') ||
        (periodType === 'custom' && moment(time).isBetween(stats.start_time, stats.end_time, 'days', '[]'))
      );
    });
  };
});

const contractStatisticsByUserId = createSelector(defaultSelectors.values, statistics => {
  let result = {};
  for (const elem of statistics) {
    result[elem.user_id] = result[elem.user_id] || [];
    result[elem.user_id].push({ ...elem.stats, loaded: elem.loaded });
  }

  return result;
});

export default {
  ...defaultSelectors,
  selectContractStatisticsFilter,
  contractStatisticsByUserId
};
